import { cn } from '@/lib/utils';
import {
  Home,
  Search,
  Inbox,
  BadgeCheck,
  Settings,
  HelpCircle,
  LogOut,
  MessageSquarePlus,
  ChevronDown,
} from 'lucide-react';
import { Link, useRouterState, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth';
import { Avatar } from '@/components/ui/avatar';
import { BRAND } from '@maintenance-tracker/shared';
import { BrandMark } from '@/components/brand/brand-logo';
import { useSupport } from '@/lib/support-context';
import { useUnsaved } from '@/lib/unsaved';
import { Button } from '@/components/ui/button';

type NavItem = {
  to: string;
  label: string;
  short: string;
  subtitle: string;
  icon: typeof Home;
  match: (p: string) => boolean;
};

const NAV: NavItem[] = [
  { to: '/app', label: 'Home', short: 'Home', subtitle: 'Next best action', icon: Home, match: (p) => p === '/app' },
  {
    to: '/app/review',
    label: 'Website Growth Review',
    short: 'Website',
    subtitle: 'Findings & fixes',
    icon: Search,
    match: (p) => p.startsWith('/app/review') || p.startsWith('/app/improvements'),
  },
  {
    to: '/app/quote-requests',
    label: 'Quote Requests',
    short: 'Quotes',
    subtitle: 'Inbox & page',
    icon: Inbox,
    match: (p) => p.startsWith('/app/quote-requests'),
  },
  {
    to: '/app/reviews-proof',
    label: 'Reviews & Project Proof',
    short: 'Proof',
    subtitle: 'Reviews & jobs',
    icon: BadgeCheck,
    match: (p) => p.startsWith('/app/reviews-proof'),
  },
  {
    to: '/app/settings',
    label: 'Settings & Your Data',
    short: 'Settings',
    subtitle: 'Account & privacy',
    icon: Settings,
    match: (p) => p.startsWith('/app/settings'),
  },
];

function HelpDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { openSupport } = useSupport();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" role="dialog" aria-modal="true">
      <button type="button" className="flex-1" aria-label="Close help" onClick={onClose} />
      <aside className="flex h-full w-full max-w-md flex-col bg-bg-surface shadow-modal">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-lg font-semibold">Help</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-5 text-sm text-text-secondary">
          <p>
            <strong className="text-text-primary">Website Growth Review</strong> — we only read public pages. Findings
            always show evidence.
          </p>
          <p>
            <strong className="text-text-primary">Quote Requests</strong> — your page, your inbox. Spam protections are
            built in.
          </p>
          <p>
            <strong className="text-text-primary">Your data</strong> — export or delete anytime from Settings.
          </p>
          <Button
            className="w-full"
            onClick={() => {
              onClose();
              openSupport('app_help');
            }}
          >
            Contact support
          </Button>
        </div>
      </aside>
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const { confirmLeave } = useUnsaved();
  const { openSupport } = useSupport();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const doLogout = async () => {
    if (!confirmLeave()) return;
    await logout();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-bg-subtle"
      >
        <Avatar name={user?.user?.name || user?.user?.email || 'U'} size="sm" />
        <ChevronDown className="hidden h-4 w-4 text-text-muted sm:block" />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 w-56 rounded-xl border border-border bg-bg-surface py-1 shadow-dropdown">
          <div className="border-b border-border px-3 py-2">
            <p className="truncate text-sm font-medium text-text-primary">{user?.user?.name}</p>
            <p className="truncate text-xs text-text-muted">{user?.user?.email}</p>
          </div>
          <a
            href="/app/settings"
            className="block px-3 py-2 text-sm text-text-secondary hover:bg-bg-subtle"
            onClick={() => setOpen(false)}
          >
            Your data
          </a>
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm text-text-secondary hover:bg-bg-subtle"
            onClick={() => {
              setOpen(false);
              openSupport('app_help');
            }}
          >
            Help / Contact
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger-subtle"
            onClick={doLogout}
          >
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

export function AppShell({ children, title }: { children: ReactNode; title?: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { confirmLeave } = useUnsaved();
  const { openSupport } = useSupport();
  const [helpOpen, setHelpOpen] = useState(false);

  const activeLabel = NAV.find((n) => n.match(pathname))?.label ?? title ?? BRAND.name;

  const go = (to: string) => {
    if (!confirmLeave()) return;
    void navigate({ to: to as '/app' });
  };

  return (
    <div className="flex min-h-screen bg-bg-app">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 flex-col bg-sidebar text-sidebar-text lg:flex">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <BrandMark className="h-8 w-8" />
          <span className="text-sm font-semibold text-white">{BRAND.name}</span>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {NAV.map((item) => {
            const active = item.match(pathname);
            const Icon = item.icon;
            return (
              <button
                key={item.to}
                type="button"
                onClick={() => go(item.to)}
                className={cn(
                  'flex w-full flex-col rounded-lg px-3 py-2.5 text-left transition-colors',
                  active
                    ? 'bg-sidebar-active text-sidebar-text-active'
                    : 'hover:bg-sidebar-hover hover:text-sidebar-text-active',
                )}
              >
                <span className="flex items-center gap-3 text-sm font-medium">
                  <Icon className="h-[18px] w-[18px] shrink-0" />
                  {item.label}
                </span>
                <span className={cn('ml-8 mt-0.5 text-[11px]', active ? 'text-blue-100/80' : 'text-sidebar-text')}>
                  {item.subtitle}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Tablet icon rail */}
      <aside className="hidden w-16 shrink-0 flex-col items-center bg-sidebar py-4 md:flex lg:hidden">
        <BrandMark className="mb-6 h-8 w-8" />
        {NAV.map((item) => {
          const active = item.match(pathname);
          const Icon = item.icon;
          return (
            <button
              key={item.to}
              type="button"
              title={item.label}
              onClick={() => go(item.to)}
              className={cn(
                'mb-1 flex h-11 w-11 items-center justify-center rounded-lg',
                active ? 'bg-sidebar-active text-white' : 'text-sidebar-text hover:bg-sidebar-hover',
              )}
            >
              <Icon className="h-5 w-5" />
            </button>
          );
        })}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col pb-20 md:pb-0">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-border bg-bg-surface/95 px-4 backdrop-blur sm:px-6">
          <h1 className="truncate text-base font-semibold text-text-primary">{activeLabel}</h1>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button variant="ghost" size="sm" onClick={() => setHelpOpen(true)} className="gap-1.5">
              <HelpCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Help</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openSupport('whats_missing', { mode: 'idea' })}
              className="gap-1.5"
            >
              <MessageSquarePlus className="h-4 w-4" />
              <span className="hidden sm:inline">Tell us what&apos;s missing</span>
            </Button>
            <UserMenu />
          </div>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6">{children}</main>
      </div>

      {/* Mobile bottom tabs */}
      <nav className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-bg-surface md:hidden">
        {NAV.map((item) => {
          const active = item.match(pathname);
          const Icon = item.icon;
          return (
            <button
              key={item.to}
              type="button"
              onClick={() => go(item.to)}
              className={cn(
                'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium',
                active ? 'text-brand' : 'text-text-muted',
              )}
            >
              <Icon className="h-5 w-5" />
                {item.short}
            </button>
          );
        })}
      </nav>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
